import type { Accommodation } from "@@/shared/acquire/accommodation";
import { accommodationZodObject } from "@@/shared/acquire/accommodation";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faBasketShopping,
    faCalendar,
    faGiftCard,
    faHouse,
    faTicket,
} from "@fortawesome/pro-regular-svg-icons";
import { z } from "zod";
import { toObjectMap } from "../../utilities/dictionary";
import { isProduct } from "../products/is-product";
import type { Product } from "../products/product";
import type {
    Translatable,
    TranslatableValue,
} from "../translations/translation";
import { translation } from "../translations/translation";
import type { Bookable } from "./bookable";
import { bookableZodObject } from "./bookable";
import type { Buyable } from "./buyable";
import { buyableZodObject } from "./buyable";
import type { GiftCard } from "./giftcard";
import { giftCardZodObject } from "./giftcard";
import type { Voucher } from "./voucher";
import { voucherZodObject } from "./voucher";

const acquireTypes = [
    "BOOKABLE",
    "BUYABLE",
    "VOUCHER",
    "GIFTCARD",
    "ACCOMMODATION",
] as const;
type AcquireType = (typeof acquireTypes)[number];
const acquireType = toObjectMap<AcquireType>([...acquireTypes]);

type AcquireOption = Buyable | Bookable | Voucher | GiftCard | Accommodation;
type Acquirable = Buyable | Bookable | Voucher | GiftCard | Accommodation;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isAcquirable = (item: any): item is Acquirable => {
    return "_type" in item && acquireTypes.some(type => type === item._type);
};

const isAcquireType = (item: string): item is AcquireType => {
    return acquireTypes.some(type => type === item);
};

const acquirableZodUnion = z.union([
    buyableZodObject,
    bookableZodObject,
    voucherZodObject,
    giftCardZodObject,
    accommodationZodObject,
]);
const acquireTypeZodSchema: z.ZodSchema<AcquireType> = z.enum([
    "BOOKABLE",
    "BUYABLE",
    "VOUCHER",
    "GIFTCARD",
    "ACCOMMODATION",
]);

const acquirableTypeName: Record<
    AcquireType,
    {
        singular: TranslatableValue;
        plural: TranslatableValue;
    }
> = {
    BOOKABLE: {
        singular: translation({
            sv: "Bokningsbar",
            en: "Bookable",
        }),
        plural: translation({
            sv: "Bokningsbara",
            en: "Bookables",
        }),
    },
    VOUCHER: {
        singular: translation({
            sv: "Värdebevis",
            en: "Voucher",
        }),
        plural: translation({
            sv: "Värdebevis",
            en: "Vouchers",
        }),
    },
    BUYABLE: {
        singular: translation({
            sv: "Köpbar",
            en: "Buyable",
        }),
        plural: translation({
            sv: "Köpbara",
            en: "Buyables",
        }),
    },
    GIFTCARD: {
        singular: translation({
            sv: "Presentkort",
            en: "Gift card",
        }),
        plural: translation({
            sv: "Presentkort",
            en: "Gift cards",
        }),
    },
    ACCOMMODATION: {
        singular: translation({
            sv: "Boende",
            en: "Accommodation",
        }),
        plural: translation({
            sv: "Boenden",
            en: "Accommodations",
        }),
    },
} as const;

function getAcquirableType(product: Product): AcquireType;
function getAcquirableType(acquirable: Acquirable): AcquireType;
function getAcquirableType(type: AcquireType): AcquireType;
function getAcquirableType(
    item: Product | AcquireType | Acquirable,
): AcquireType;
function getAcquirableType(
    item: Product | AcquireType | Acquirable,
): AcquireType {
    const type = isProduct(item)
        ? item.acquirableAs
        : isAcquirable(item)
          ? item._type
          : item;

    return type;
}

function getAcquirableTypeName(
    product: Product,
    numerus?: "singular" | "plural",
): Translatable;
function getAcquirableTypeName(
    acquirable: Acquirable,
    numerus?: "singular" | "plural",
): Translatable;
function getAcquirableTypeName(
    type: AcquireType,
    numerus?: "singular" | "plural",
): Translatable;
function getAcquirableTypeName(
    item: Product | AcquireType | Acquirable,
    numerus: "singular" | "plural" = "singular",
): Translatable {
    const type = getAcquirableType(item);
    const result = acquirableTypeName[type][numerus];
    if (!result) throw new Error("Unknown type; " + type);
    return result;
}

const acquirableTypeIcon: Record<AcquireType, IconDefinition> = {
    BOOKABLE: faCalendar,
    BUYABLE: faBasketShopping,
    GIFTCARD: faGiftCard,
    VOUCHER: faTicket,
    ACCOMMODATION: faHouse,
} as const;

function getAcquirableTypeIcon(product: Product): IconDefinition;
function getAcquirableTypeIcon(acquirable: Acquirable): IconDefinition;
function getAcquirableTypeIcon(type: AcquireType): IconDefinition;
function getAcquirableTypeIcon(
    item: Product | AcquireType | Acquirable,
): IconDefinition {
    const type = getAcquirableType(item);
    const result = acquirableTypeIcon[type];
    if (!result) throw new Error("Unknown type; " + type);
    return result;
}

type ProductType = AcquireType;
const productType = acquireType;
const productTypes = acquireTypes;
const isProductType = isAcquireType;
const productTypeZodSchema = acquireTypeZodSchema;
const productTypeName = acquirableTypeName;
const getProductTypeName = getAcquirableTypeName;
const productTypeIcon = acquirableTypeIcon;
const getProductTypeIcon = getAcquirableTypeIcon;

export {
    acquirableTypeIcon,
    acquirableTypeName,
    acquirableZodUnion,
    acquireType,
    acquireTypes,
    acquireTypeZodSchema,
    getAcquirableTypeIcon,
    getAcquirableTypeName,
    getProductTypeIcon,
    getProductTypeName,
    isAcquirable,
    isAcquireType,
    isProductType,
    productType,
    productTypeIcon,
    productTypeName,
    productTypes,
    productTypeZodSchema,
};
export type { Acquirable, AcquireOption, AcquireType, ProductType };
